import React, {useEffect, useState} from 'react';
import './styles.css';
import FormField from '../components/FormField';
import Button from '../../../../uiToolkit/Buttons/Button';
import SpecialBrowse from '../components/SpecialBrowse';
import {MyLibraryModal} from '../../../../modules/MyLibraryModal';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import AvatarLibrary from '../components/AvatarLibrary';
import TitleInput from '../components/TitleInput';
import {generateAiAssistantText} from '../generateAiAssisntantText';
import {
  AI_OPTION_DIRECT,
  AI_OPTION_INDIRECT,
  AiAssistantCommunicationOptions,
  AiAssistantResponseOptions,
} from '../../../../data/models/AI/AiAssisntantOptions';
import {
  AiAssistantEditModel,
  CreateAiAssistantModel,
} from '../../../../data/models/AI/AiAssistantModels';
import SearchDropdown from '../components/SearchDropdown';
import ToggleSwitch from '../../../../uiToolkit/Inputs/ToggleSwitch';
import Dropdown from '../../../../uiToolkit/Inputs/Dropdown';
import {CourseShortListModel} from '../../../../data/models/Course/CourseList';
import PageTitle from '../../../../uiToolkit/PageTitle';
import {tapyblAiHomeText} from '../../Home/tapyblAiHomeText';

interface Props {
  courseSearch: string;
  courses: any;
  setSearchText: (config: string) => void;
  onSave: (
    model: CreateAiAssistantModel,
    avatar?: File,
    deleteAvatar?: boolean,
  ) => void;
  onCancel: any;
  isLoading: boolean;
  organizationAvatar?: string;
  editing?: boolean;
  assistant?: AiAssistantEditModel;
}

const GenerateAIAssistantUI = React.memo(
  ({
    courseSearch,
    setSearchText,
    courses,
    onSave,
    onCancel,
    isLoading,
    organizationAvatar,
    editing,
    assistant,
  }: Props) => {
    const [sourceFiles, setSourceFiles] = useState<MediaFile[]>([]);
    const [avatarFile, setAvatarFile] = useState<File>();
    const [existingAvatarFile, setExistingAvatarFile] = useState<string>();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [scopeIds, setScopeIds] = useState<string[]>([]);
    const [responseStyle, setResponseStyle] =
      useState<string>(AI_OPTION_DIRECT);
    const [communicationTone, setCommunicationTone] =
      useState<string>('formal');
    const [showErrors, setShowErrors] = useState(false);

    useEffect(() => {
      if (assistant) {
        setTitle(assistant.title);
        setDescription(assistant.description || '');
        if (assistant.isGlobal) {
          setScopeIds(['workspace', ...assistant.scopes]);
        } else {
          setScopeIds(assistant.scopes);
        }
        setResponseStyle(
          assistant.directAnswer ? AI_OPTION_DIRECT : AI_OPTION_INDIRECT,
        );
        setExistingAvatarFile(assistant.imageUrl);
        setCommunicationTone(assistant.conversationalTone || 'formal');
        setSourceFiles(assistant.files);
      }
    }, [assistant]);

    const isCommunicationToneValid = () => communicationTone.trim() !== '';
    const isTitleValid = () => title.trim() !== '';
    const hasSourceFiles = () => sourceFiles.length > 0;
    const isFormValid = () =>
      isTitleValid() && hasSourceFiles() && isCommunicationToneValid();

    const handleSave = () => {
      if (!isFormValid()) {
        setShowErrors(true);
        return;
      }
      const model: CreateAiAssistantModel = {
        title: title,
        description: description,
        conversationalTone: communicationTone,
        scopeIds: scopeIds,
        libraryMediaFileIds: sourceFiles.map(x => x.id),
        directAnswer: responseStyle === AI_OPTION_DIRECT,
      };
      onSave(model, avatarFile, !avatarFile && !existingAvatarFile);
    };

    const searchableSelectOptions = courses.map(
      (course: CourseShortListModel) => ({
        key: course.id,
        value: course.title,
        thumb: course.avatarUrl,
      }),
    );

    const saveEnabled = () => isFormValid();

    const onAvatarDelete = () => {
      setAvatarFile(undefined);
      setExistingAvatarFile(undefined);
    };

    return (
      <div className="GenerateAIAssistantContainer">
        <PageTitle
          title={tapyblAiHomeText.title}
          subtitle={tapyblAiHomeText.subtitle}
        />

        <main className="GenerateAIAssistantContent">
          <section className="GenerateAIAssistantFormHeader">
            <h1>
              {editing
                ? generateAiAssistantText.editTitle
                : generateAiAssistantText.createTitle}
            </h1>
          </section>

          <section className="GenerateAIAssistantFormBody">
            <div className="GenerateAIAssistantFormContent">
              <div className="GenerateAIAssistantFormInnerHeader">
                <AvatarLibrary
                  onFileSelected={setAvatarFile}
                  file={avatarFile}
                  existingFileUrl={existingAvatarFile}
                  onAvatarDelete={onAvatarDelete}
                />
                <div className="GenerateAIAssistantTitleDesc">
                  <TitleInput value={title} setValue={setTitle} />
                  <textarea
                    name="GenerateAIAssistantDescriptionField"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    maxLength={300}
                    placeholder={
                      generateAiAssistantText.descriptionPlaceholder
                    }></textarea>
                  <div className="TitleInputMaxCharacters">
                    <span>{description.length}/300</span>
                  </div>
                </div>
              </div>

              <SpecialBrowse
                onFilesSelected={setSourceFiles}
                sourceFiles={sourceFiles}
              />

              <FormField
                label={generateAiAssistantText.visibility}
                tooltip={generateAiAssistantText.visibilityTooltip}>
                <SearchDropdown
                  options={searchableSelectOptions}
                  selectedOptions={scopeIds}
                  setSelectedOptions={setScopeIds}
                  placeholder={generateAiAssistantText.visibilityPlaceholder}
                  fieldError={false}
                  className="SearchDropdown_Container"
                  onSearch={setSearchText}
                  search={courseSearch}
                  specialOption={{
                    key: 'workspace',
                    value: 'All workspace',
                    thumb: organizationAvatar,
                  }}
                />
              </FormField>
              <FormField
                label={generateAiAssistantText.responseStyle}
                tooltip={generateAiAssistantText.responseStyleTooltip}>
                <ToggleSwitch
                  options={AiAssistantResponseOptions}
                  onChange={setResponseStyle}
                  selectedKey={responseStyle}
                />
              </FormField>
              <FormField label={generateAiAssistantText.communicationTone}>
                <Dropdown
                  options={AiAssistantCommunicationOptions}
                  selectedOption={communicationTone}
                  setSelectedOption={setCommunicationTone}
                  placeholder="Select tone"
                  fieldError={showErrors && !isCommunicationToneValid()}
                  isMandatory
                />
              </FormField>
            </div>
            <div className="GenerateAIAssistantActionButtons">
              <Button title="Cancel" onClick={onCancel} uiType="text" />
              <Button
                title="Save"
                onClick={handleSave}
                uiType="action"
                isLoading={isLoading}
                disabled={!saveEnabled()}
              />
            </div>
          </section>
        </main>
        <MyLibraryModal />
      </div>
    );
  },
);

export default GenerateAIAssistantUI;
